/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { UserRegistration } from '@sportninja/common/hooks/useUserRegistrationData'
import DataGrid from 'src/components/ts-components/DataGrid'
import {
  REGISTRATION_CELL_CLASS,
  REGISTRATION_CELL_END_CLASS,
  useRegistrationColumns,
} from '../hooks/useRegistrationColumns'

/** Holds a two-line competition name over its dates. */
const ROW_HEIGHT = 72
const HEADER_HEIGHT = 44

interface RegistrationsGridProps {
  /** The rows on show. */
  rows: UserRegistration[]
  /** Every registration, so the Competition filter lists them all. */
  registrations: UserRegistration[]
  isMobile: boolean
  onResumeSession: (record: UserRegistration) => void
  onDeleteSession: (record: UserRegistration) => void
}

const getRowId = ({ data }: { data: UserRegistration }) => data.uid

/**
 * `domLayout='autoHeight'` rather than a fixed height: the page scrolls and adds
 * rows as it nears the bottom, so the grid grows instead of scrolling itself.
 */
const RegistrationsGrid = ({
  rows,
  registrations,
  isMobile,
  onResumeSession,
  onDeleteSession,
}: RegistrationsGridProps) => {
  'use memo'

  const columnDefs = useRegistrationColumns({
    registrations,
    isMobile,
    onResumeSession,
    onDeleteSession,
  })

  return (
    <div css={[gridStyle, isMobile && mobileGridStyle]}>
      <DataGrid<UserRegistration>
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={{ cellClass: REGISTRATION_CELL_CLASS }}
        getRowId={getRowId}
        domLayout='autoHeight'
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        suppressCellFocus
        horizontalScrollAffordance
        stickyHorizontalScrollbar
        data-testid='my-registrations-grid'
      />
    </div>
  )
}

export default RegistrationsGrid

// ─── Styles ──────────────────────────────────────────────────────

const gridStyle = css`
  width: 100%;
  min-width: 0;

  /* rowHeight sizes the row box only; cell line-height follows the theme's
     --ag-row-height, declared on .ag-root-wrapper because the theme sets it on
     an element below the outer wrapper. */
  .ag-root-wrapper {
    --ag-row-height: ${ROW_HEIGHT}px;
  }

  .${REGISTRATION_CELL_CLASS} {
    display: flex;
    align-items: center;
  }

  .${REGISTRATION_CELL_END_CLASS} {
    justify-content: flex-end;
  }
`

const mobileGridStyle = css`
  padding: 0 16px;
`
